import { useState, useEffect } from 'react';
import { collection, query, orderBy, getDocs, doc, updateDoc, where } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuthContext } from '../contexts/AuthContext';
import { Flag, CheckCircle, Clock, AlertCircle, ExternalLink, Trash2 } from 'lucide-react';

interface VideoReport {
  id: string;
  userId: string;
  userName: string;
  userEmail: string;
  courseId: string;
  courseName: string;
  lessonId: string;
  lessonTitle: string;
  lessonOrderIndex: number;
  videoUrl: string;
  videoFileName?: string;
  videoJobId: string | null;
  message: string;
  status: 'pending' | 'resolved' | 'rejected';
  createdAt: any;
  instructorId: string;
  resolvedAt?: any;
  resolvedBy?: string;
}

export default function VideoReportsInstructor() {
  const { user } = useAuthContext();
  const [reports, setReports] = useState<VideoReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'pending' | 'resolved' | 'rejected'>('all');

  useEffect(() => {
    if (user) {
      loadReports();
    }
  }, [user]);

  const loadReports = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      const reportsRef = collection(db, 'video_reports');
      const q = query(
        reportsRef, 
        where('instructorId', '==', user.uid),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(q);
      
      const reportsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as VideoReport[];
      
      setReports(reportsData);
    } catch (error) {
      console.error('Error loading reports:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateReportStatus = async (reportId: string, status: 'resolved' | 'rejected') => {
    try {
      const reportRef = doc(db, 'video_reports', reportId);
      await updateDoc(reportRef, {
        status,
        resolvedAt: new Date(),
        resolvedBy: user?.uid || 'instructor'
      });
      
      // Update local state
      setReports(prev => prev.map(r => 
        r.id === reportId 
          ? { ...r, status, resolvedAt: new Date(), resolvedBy: user?.uid || 'instructor' }
          : r
      ));
    } catch (error) {
      console.error('Error updating report:', error);
      alert('เกิดข้อผิดพลาดในการอัปเดตสถานะ');
    }
  };

  const filteredReports = reports.filter(r => {
    if (filter === 'all') return true;
    return r.status === filter;
  });

  const pendingCount = reports.filter(r => r.status === 'pending').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">กำลังโหลดรายงาน...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">รายงานปัญหาวิดีโอ</h2>
          <p className="text-gray-600 mt-1">รายงานปัญหาจากผู้เรียนในคอร์สของคุณ</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="bg-red-100 text-red-700 px-4 py-2 rounded-lg font-semibold">
            <Flag className="w-4 h-4 inline mr-2" />
            {pendingCount} รอดำเนินการ
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-2">
        <button
          onClick={() => setFilter('all')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            filter === 'all'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          ทั้งหมด ({reports.length})
        </button>
        <button
          onClick={() => setFilter('pending')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            filter === 'pending'
              ? 'bg-red-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          รอดำเนินการ ({pendingCount})
        </button>
        <button
          onClick={() => setFilter('resolved')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            filter === 'resolved'
              ? 'bg-green-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          แก้ไขแล้ว ({reports.filter(r => r.status === 'resolved').length})
        </button>
        <button
          onClick={() => setFilter('rejected')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            filter === 'rejected'
              ? 'bg-gray-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          ปฏิเสธ ({reports.filter(r => r.status === 'rejected').length})
        </button>
      </div>

      {/* Reports List */}
      <div className="space-y-4">
        {filteredReports.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-xl">
            <Flag className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-600">ไม่มีรายงานในหมวดนี้</p>
          </div>
        ) : (
          filteredReports.map(report => (
            <div
              key={report.id}
              className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      report.status === 'pending'
                        ? 'bg-red-100 text-red-700'
                        : report.status === 'resolved'
                        ? 'bg-green-100 text-green-700'
                        : 'bg-gray-100 text-gray-700'
                    }`}>
                      {report.status === 'pending' && <Clock className="w-3 h-3 inline mr-1" />}
                      {report.status === 'resolved' && <CheckCircle className="w-3 h-3 inline mr-1" />}
                      {report.status === 'rejected' && <Trash2 className="w-3 h-3 inline mr-1" />}
                      {report.status === 'pending' ? 'รอดำเนินการ' : report.status === 'resolved' ? 'แก้ไขแล้ว' : 'ปฏิเสธ'}
                    </span>
                    <span className="text-xs text-gray-500">
                      {report.createdAt?.toDate().toLocaleDateString('th-TH', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">
                    {report.courseName}
                  </h3>
                  <p className="text-sm text-gray-600 mb-1">
                    EP {report.lessonOrderIndex}: {report.lessonTitle}
                  </p>
                  {(report.videoFileName || report.videoUrl) && (
                    <p className="text-xs text-gray-500 mb-1 font-mono bg-blue-50 px-2 py-1 rounded inline-block border border-blue-200">
                      📁 ไฟล์: {report.videoFileName || (report.videoUrl ? decodeURIComponent(report.videoUrl.split('/').pop()?.split('?')[0] || 'N/A').replace(/^\d+-/, '') : 'N/A')}
                    </p>
                  )}
                  {report.videoJobId && (
                    <p className="text-xs text-gray-500 mb-2 font-mono bg-gray-100 px-2 py-1 rounded inline-block">
                      📹 Video ID: {report.videoJobId}
                    </p>
                  )}
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-3 mt-2">
                    <p className="text-sm text-gray-700">{report.message}</p>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-gray-500">
                    <span>รายงานโดย: {report.userName} ({report.userEmail})</span>
                  </div>
                </div>
              </div>

              {/* Actions */}
              {report.status === 'pending' && (
                <div className="flex gap-2 pt-4 border-t border-gray-200">
                  <button
                    onClick={() => updateReportStatus(report.id, 'resolved')}
                    className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
                  >
                    <CheckCircle className="w-4 h-4" />
                    ทำเครื่องหมายว่าแก้ไขแล้ว
                  </button>
                  <button
                    onClick={() => updateReportStatus(report.id, 'rejected')}
                    className="flex-1 px-4 py-2 bg-gray-600 text-white rounded-lg font-medium hover:bg-gray-700 transition-colors flex items-center justify-center gap-2"
                  >
                    <Trash2 className="w-4 h-4" />
                    ปฏิเสธ
                  </button>
                  <a
                    href={`/courses/${report.courseId}/learn/${report.lessonOrderIndex}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center gap-2"
                  >
                    <ExternalLink className="w-4 h-4" />
                    ดูบทเรียน
                  </a>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
